import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Application, ApplicationStatus, Job } from '@etnrlz-jobs/shared';
import { api } from '../lib/api';
import {
  AppStatusBadge,
  ErrorBox,
  FitScore,
  fmtDateTime,
  PageHeader,
  RecommendationBadge,
  Spinner,
} from '../components/ui';
import { Markdown } from '../components/Markdown';

interface AppResponse {
  application: Application;
  resumeUrl: string | null;
  videoUrl: string | null;
}

export function ApplicationDetailPage() {
  const { id, appId } = useParams<{ id: string; appId: string }>();
  const [data, setData] = useState<AppResponse | null>(null);
  const [job, setJob] = useState<Job | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, j] = await Promise.all([
        api<AppResponse>(`/admin/jobs/${id}/applications/${appId}`),
        api<{ job: Job }>(`/admin/jobs/${id}`),
      ]);
      setData(a);
      setJob(j.job);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar');
    }
  }, [id, appId]);

  useEffect(() => {
    void load();
  }, [load]);

  const app = data?.application;

  // Poll enquanto a IA avalia.
  useEffect(() => {
    if (!app || (app.fitStatus !== 'pending' && app.fitStatus !== 'processing')) return;
    const t = window.setInterval(() => void load(), 4000);
    return () => window.clearInterval(t);
  }, [app, load]);

  const setStatus = async (status: ApplicationStatus) => {
    setBusy(true);
    try {
      await api(`/admin/jobs/${id}/applications/${appId}/status`, {
        method: 'POST',
        body: { status },
      });
      await load();
    } finally {
      setBusy(false);
    }
  };

  const rescore = async () => {
    setBusy(true);
    try {
      await api(`/admin/jobs/${id}/applications/${appId}/score`, { method: 'POST' });
      await load();
    } finally {
      setBusy(false);
    }
  };

  if (!app || !job) {
    return error ? (
      <ErrorBox message={error} />
    ) : (
      <div className="flex justify-center py-16">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <Link to={`/vagas/${id}`} className="text-xs text-slate-400 hover:text-slate-200">
          ← {job.title}
        </Link>
      </div>
      <PageHeader
        title={app.name}
        subtitle={`Candidatura em ${fmtDateTime(app.createdAt)}`}
        actions={<AppStatusBadge status={app.status} />}
      />
      {error && (
        <div className="mb-4">
          <ErrorBox message={error} />
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* Avaliação de fit */}
          <section className="card">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-semibold text-slate-100">Fit com a vaga (IA)</h2>
              <button className="btn-secondary text-xs" disabled={busy} onClick={rescore}>
                Reavaliar
              </button>
            </div>
            {(app.fitStatus === 'pending' || app.fitStatus === 'processing') && (
              <div className="flex items-center gap-3 py-6 text-sm text-slate-400">
                <Spinner className="h-5 w-5 text-brand-400" />
                A IA está avaliando esta candidatura…
              </div>
            )}
            {app.fitStatus === 'error' && (
              <ErrorBox message={`Falha na avaliação: ${app.fitError ?? 'erro desconhecido'}`} />
            )}
            {app.fitStatus === 'done' && app.fit && (
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <FitScore score={app.fit.score} />
                  <RecommendationBadge rec={app.fit.recommendation} />
                  <span className="text-xs text-slate-500">
                    {app.fit.model} · {fmtDateTime(app.fit.scoredAt)}
                  </span>
                </div>
                <p className="text-sm leading-relaxed text-slate-300">{app.fit.summary}</p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-emerald-400">
                      Pontos fortes
                    </h3>
                    <ul className="list-disc space-y-1 pl-5 text-sm text-slate-300">
                      {app.fit.strengths.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-400">
                      Pontos de atenção
                    </h3>
                    <ul className="list-disc space-y-1 pl-5 text-sm text-slate-300">
                      {app.fit.concerns.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* Respostas */}
          <section className="card">
            <h2 className="mb-4 font-semibold text-slate-100">Respostas de triagem</h2>
            <div className="space-y-4">
              {job.questions.map((q) => {
                const ans = app.answers.find((a) => a.questionId === q.id);
                return (
                  <div key={q.id}>
                    <p className="text-xs font-medium text-slate-400">{q.label}</p>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-slate-200">
                      {ans?.value || <span className="text-slate-500">(sem resposta)</span>}
                    </p>
                  </div>
                );
              })}
            </div>
          </section>

          {job.tests.length > 0 && (
            <section className="card">
              <h2 className="mb-4 font-semibold text-slate-100">Testes práticos</h2>
              <div className="space-y-6">
                {job.tests.map((t) => {
                  const ans = app.testAnswers.find((a) => a.testId === t.id);
                  return (
                    <div key={t.id}>
                      <p className="text-sm font-semibold text-slate-100">{t.title}</p>
                      <div className="mt-1 rounded-lg bg-ink-950/60 p-3 text-xs text-slate-400">
                        <Markdown text={t.instructions} />
                      </div>
                      <p className="mt-2 whitespace-pre-wrap text-sm text-slate-200">
                        {ans?.value || <span className="text-slate-500">(sem resposta)</span>}
                      </p>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>

        {/* Coluna lateral */}
        <div className="space-y-6">
          <section className="card space-y-2 text-sm">
            <h2 className="mb-2 font-semibold text-slate-100">Contato</h2>
            <p>
              <a href={`mailto:${app.email}`} className="text-brand-400 hover:underline">
                {app.email}
              </a>
            </p>
            {app.phone && <p className="text-slate-300">{app.phone}</p>}
            {app.linkedin && (
              <p>
                <a
                  href={app.linkedin.startsWith('http') ? app.linkedin : `https://${app.linkedin}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand-400 hover:underline"
                >
                  LinkedIn
                </a>
              </p>
            )}
            {app.instagram && (
              <p>
                <a
                  href={
                    app.instagram.startsWith('http')
                      ? app.instagram
                      : `https://instagram.com/${app.instagram.replace(/^@/, '')}`
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand-400 hover:underline"
                >
                  Instagram
                </a>
              </p>
            )}
            {app.portfolio && (
              <p>
                <a
                  href={
                    app.portfolio.startsWith('http') ? app.portfolio : `https://${app.portfolio}`
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand-400 hover:underline"
                >
                  Portfólio
                </a>
              </p>
            )}
          </section>

          {data?.videoUrl && (
            <section className="card">
              <h2 className="mb-3 font-semibold text-slate-100">Vídeo de apresentação</h2>
              <video src={data.videoUrl} controls className="w-full rounded-lg" />
            </section>
          )}

          {data?.resumeUrl && (
            <section className="card">
              <h2 className="mb-3 font-semibold text-slate-100">Currículo</h2>
              <a href={data.resumeUrl} target="_blank" rel="noreferrer" className="btn-secondary w-full">
                Abrir PDF
              </a>
            </section>
          )}

          <section className="card space-y-2">
            <h2 className="mb-1 font-semibold text-slate-100">Triagem</h2>
            <button
              className="btn-primary w-full"
              disabled={busy || app.status === 'shortlisted'}
              onClick={() => setStatus('shortlisted')}
            >
              Mover para shortlist
            </button>
            <button
              className="btn-secondary w-full"
              disabled={busy || app.status === 'reviewed'}
              onClick={() => setStatus('reviewed')}
            >
              Marcar como analisado
            </button>
            <button
              className="btn-danger w-full"
              disabled={busy || app.status === 'rejected'}
              onClick={() => setStatus('rejected')}
            >
              Descartar
            </button>
          </section>
        </div>
      </div>
    </div>
  );
}
